// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';

// material ui
import { Box, Button, ButtonGroup, Tooltip } from '@mui/material';

// api
import { useAlarmsPatchAsyncByidalarmMutation } from '@/api/generated/recalc/endpoints/v1/alarms';
import {
  type AlarmState,
  type GetAlarmDto,
  recalcV2OpenApi,
} from '@/api/generated/recalc/endpoints/v2/alarms';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

type EnergyXplorerAlarmStateActionsProps = {
  accountId: string | undefined;
  alarm: GetAlarmDto;
};

export default function EnergyXplorerAlarmStateActions({
  accountId,
  alarm,
}: EnergyXplorerAlarmStateActionsProps) {
  const { t } = useTranslation(['energy-analysis', 'alarms']);
  const dispatch = useDispatch();

  const [updateAlarmState, { isLoading: isUpdating }] =
    useAlarmsPatchAsyncByidalarmMutation();

  const hasValidAccountId = Number.isFinite(Number(accountId));

  const alarmId = alarm.alarmId ?? null;
  const currentState = alarm.state ?? 'Unresolved';

  const actionsDisabled = !alarmId || !hasValidAccountId || isUpdating;

  const resolveLabel =
    currentState === 'Solved'
      ? t('state.resolved', { ns: 'alarms' })
      : t('portfolio.deepdive_alarms.actions.resolve', {
          ns: 'energy-analysis',
        });
  const dismissLabel = t('portfolio.deepdive_alarms.actions.dismiss', {
    ns: 'energy-analysis',
  });
  const resetLabel = t('portfolio.deepdive_alarms.actions.reset', {
    ns: 'energy-analysis',
  });

  const handleUpdateState = async (
    nextState: AlarmState,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    event.stopPropagation();

    if (!alarmId || !hasValidAccountId || isUpdating) {
      return;
    }

    const result = await updateAlarmState({
      id: alarmId,
      editAlarmDto: {
        State: nextState,
      },
    });

    if (!('error' in result)) {
      dispatch(recalcV2OpenApi.util.invalidateTags(['Alarms']));

      dispatch(
        showSnackbar({
          message: t('messages.alarm_updated', {
            ns: 'alarms',
          }),
          severity: 'success',
        }),
      );
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      m="auto"
      onClick={(event) => event.stopPropagation()}
    >
      <ButtonGroup variant="outlined" size="small" disableElevation>
        <Tooltip
          title={t('portfolio.deepdive_alarms.actions.resolve_tooltip', {
            ns: 'energy-analysis',
          })}
        >
          <span>
            <Button
              onClick={(event) => void handleUpdateState('Solved', event)}
              disabled={actionsDisabled || currentState === 'Solved'}
              startIcon={
                <CheckCircleRoundedIcon
                  fontSize="small"
                  sx={{ color: 'success.main' }}
                />
              }
            >
              {resolveLabel}
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={t('portfolio.deepdive_alarms.actions.dismiss_tooltip', {
            ns: 'energy-analysis',
          })}
        >
          <span>
            <Button
              aria-label={dismissLabel}
              onClick={(event) => void handleUpdateState('Reject', event)}
              disabled={actionsDisabled || currentState === 'Reject'}
            >
              <NotificationsOffRoundedIcon
                fontSize="small"
                sx={{ color: 'text.secondary' }}
              />
            </Button>
          </span>
        </Tooltip>
      </ButtonGroup>

      <Tooltip
        title={t('portfolio.deepdive_alarms.actions.reset_tooltip', {
          ns: 'energy-analysis',
        })}
      >
        <span>
          <Button
            variant="outlined"
            size="small"
            aria-label={resetLabel}
            onClick={(event) => void handleUpdateState('Reset', event)}
            disabled={actionsDisabled || currentState === 'Reset'}
          >
            <UndoRoundedIcon fontSize="small" sx={{ color: 'info.main' }} />
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
